import type { GroupByField } from '@/shared/groupBy'
import type { ListChromeId, PinSlot } from '@/shared/form/commandPin'
import type { ListViewKind } from '@/shared/listViews/viewCandidates'
import type { Density } from '@/shared/density/densityTokens'
import type { ColumnPrefs } from '@/runtime/useColumnPrefs'
import type { SavedView } from '@/runtime/useSavedViews'
import type { ListFormFilterField } from '@/designer/types/form.types'

export type ListSettingsTabId = 'appearance' | 'filters' | 'grouping' | 'columns' | 'views'

export interface CompositeFilterMeta {
  baseName: string
  refTargets: string[]
  primitives: string[]
}

export interface ListSettingsValue {
  view: ListViewKind
  availableViews: ListViewKind[]
  density: Density
  filters: Record<string, string>
  groupByFields: string[]
  groupByPalette: GroupByField[]
  columns: { name: string; title: string }[]
  columnPrefs: ColumnPrefs
  pins: Record<ListChromeId, PinSlot>
}

export function draftsEqual(a: ListSettingsValue, b: ListSettingsValue): boolean {
  if (a.view !== b.view || a.density !== b.density) return false
  if (!sameRecord(a.filters, b.filters)) return false
  if (!sameStrings(a.groupByFields, b.groupByFields)) return false
  if (!sameStrings(a.columnPrefs.order, b.columnPrefs.order)) return false
  if (!sameStrings(a.columnPrefs.hidden, b.columnPrefs.hidden)) return false
  if (!sameWidths(a.columnPrefs.widths, b.columnPrefs.widths)) return false
  if (a.pins.View !== b.pins.View) return false
  if (a.pins.Filters !== b.pins.Filters) return false
  if (a.pins.GroupBy !== b.pins.GroupBy) return false
  if (a.pins.Columns !== b.pins.Columns) return false
  if (a.pins.Density !== b.pins.Density) return false
  return true
}

export interface ListSettingsDialogProps {
  open: boolean
  initialTab?: ListSettingsTabId
  value: ListSettingsValue
  draftEpoch: number
  onClose: () => void
  onApply: (next: ListSettingsValue) => void
  onTabChange?: (tab: ListSettingsTabId) => void
  savedViews: SavedView[]
  activeViewId: string | null
  onApplySavedView: (view: SavedView | null) => void
  onRemoveSavedView: (id: string) => void
  onSaveView: (name: string, snapshot: ListSettingsValue) => void
  filterFields?: ListFormFilterField[]
  filterOptionsByField?: Record<string, Array<{ value: string; label: string }>>
  compositeFilterMeta?: Record<string, CompositeFilterMeta>
  connectionId?: string
}

function sameRecord(a: Record<string, string>, b: Record<string, string>): boolean {
  const keysA = Object.keys(a)
  const keysB = Object.keys(b)
  if (keysA.length !== keysB.length) return false
  return keysA.every((k) => a[k] === b[k])
}

function sameStrings(a: readonly string[], b: readonly string[]): boolean {
  if (a.length !== b.length) return false
  return a.every((item, i) => item === b[i])
}

function sameWidths(a: Record<string, number>, b: Record<string, number>): boolean {
  const keysA = Object.keys(a)
  const keysB = Object.keys(b)
  if (keysA.length !== keysB.length) return false
  return keysA.every((k) => a[k] === b[k])
}
